import React, { useState } from 'react';

import { X } from 'lucide-react';
import { queryCache, useMutation } from 'react-query';
import { Toast } from 'src/common/components';

import { createRecipientCohort, patchRecipientCohort } from '../api';
import {
  DEFAULT_SEND_LIST_BY_PURPOSE,
  EDITION_AUDIENCE_OPTIONS,
  RECIPIENT_COHORT_PURPOSE_LABEL,
  RECIPIENT_COHORT_PURPOSE_OPTIONS,
  RECIPIENT_COHORTS_QUERY_KEY,
} from '../constants';
import {
  EditionAudience,
  RecipientCohort,
  RecipientCohortPatchPayload,
  RecipientCohortPurpose,
} from '../types';
import { getCohortErrorMessage } from '../utils';

type RecipientCohortFormModalProps = {
  cohort?: RecipientCohort;
  onClose: () => void;
  onSaved: (cohort: RecipientCohort) => void;
};

const RecipientCohortFormModal: React.FC<RecipientCohortFormModalProps> = ({
  cohort,
  onClose,
  onSaved,
}) => {
  const isEditing = !!cohort;
  const [name, setName] = useState(cohort?.name ?? '');
  const [description, setDescription] = useState(cohort?.description ?? '');
  const [purpose, setPurpose] = useState<RecipientCohortPurpose>(cohort?.purpose ?? 'marketing');
  const [sendList, setSendList] = useState<EditionAudience>(
    cohort?.send_list ?? DEFAULT_SEND_LIST_BY_PURPOSE.marketing
  );
  const [isSendListTouched, setIsSendListTouched] = useState(isEditing);

  const [save, { isLoading }] = useMutation(
    () => {
      if (cohort) {
        const payload: RecipientCohortPatchPayload = {
          name: name.trim(),
          description: description.trim(),
        };
        if (sendList !== cohort.send_list) {
          payload.send_list = sendList;
        }
        return patchRecipientCohort(cohort.id, payload);
      }
      return createRecipientCohort({
        name: name.trim(),
        description: description.trim(),
        purpose,
        send_list: sendList,
      });
    },
    {
      onSuccess: (res) => {
        void queryCache.invalidateQueries(RECIPIENT_COHORTS_QUERY_KEY);
        Toast.success(isEditing ? 'Cohort actualizado.' : 'Cohort creado.');
        onSaved(res.data);
      },
      onError: (error) => {
        Toast.error(
          getCohortErrorMessage(
            error,
            isEditing ? 'No se pudo guardar el cohort.' : 'No se pudo crear el cohort.'
          )
        );
      },
    }
  );

  const changePurpose = (next: RecipientCohortPurpose): void => {
    setPurpose(next);
    if (!isSendListTouched) {
      setSendList(DEFAULT_SEND_LIST_BY_PURPOSE[next]);
    }
  };

  return (
    <div className="bg-black/40 fixed inset-0 z-50 flex items-center justify-center">
      <div className="rounded-2xl w-full max-w-lg mx-4 overflow-hidden bg-white shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="text-base font-bold text-gray-900">
            {isEditing ? 'Editar cohort de personas' : 'Nuevo cohort de personas'}
          </h3>
          <button type="button" className="btn btn-ghost p-1 text-gray-400" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <label className="block">
            <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Nombre
            </span>
            <input
              className="input mt-0.5 w-full text-sm"
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Descripción
            </span>
            <textarea
              className="input mt-0.5 w-full text-sm"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Propósito
            </span>
            {isEditing ? (
              <p className="mt-0.5 text-sm text-gray-700">
                {cohort?.purpose ? RECIPIENT_COHORT_PURPOSE_LABEL[cohort.purpose] : '—'}
              </p>
            ) : (
              <select
                className="input mt-0.5 w-full text-sm"
                value={purpose}
                onChange={(e) => changePurpose(e.target.value as RecipientCohortPurpose)}
              >
                {RECIPIENT_COHORT_PURPOSE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}
          </label>

          <div>
            <label className="block">
              <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                Lista de bajas
              </span>
              <select
                className="input mt-0.5 w-full text-sm"
                value={sendList}
                onChange={(e) => {
                  setSendList(e.target.value as EditionAudience);
                  setIsSendListTouched(true);
                }}
              >
                {EDITION_AUDIENCE_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <span className="block mt-1 text-xs text-gray-400">
              Quien se haya dado de baja de esta lista no recibirá los emails de este cohort.
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={isLoading || name.trim() === ''}
            onClick={() => save()}
          >
            {isLoading ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecipientCohortFormModal;
